import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { CarMakerService } from 'src/app/services/car-maker/car-maker.service';
import { CarMakerModalService } from 'src/app/services/car-maker-modal/car-maker-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-makes',
  templateUrl: './makes.component.html',
  styleUrls: ['./makes.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class MakesComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  carMakers: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedCarMakers: string[] = [];

  // Form data for editable fields
  formData: { [carMakerId: string]: any } = {};
  hasChanges: boolean = false;
  changedRows: any[] = [];

  constructor(
    private _carMakerService: CarMakerService,
    private _carMakerModalService: CarMakerModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadCarMakers();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadCarMakers(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedCarMakers = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._carMakerService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.carMakers = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
            this.initializeFormData(); // Initialize form data after loading
          } else {
            this.carMakers = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading car makers:', error);
          this.carMakers = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  private initializeFormData(): void {
    this.formData = {};
    this.carMakers.forEach(carMaker => {
      this.formData[carMaker._id] = {
        show_on_home: carMaker.show_on_home || false
      };
    });
  }

  onPageChange(page: number): void {
    this.loadCarMakers(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadCarMakers(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadCarMakers();
  }

  openAddCarMakerModal(): void {
    this._carMakerModalService.clearSelectedCarMaker();
    const modalElement = document.getElementById('kt_modal_car_maker_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(carMaker: any): void {
    this._carMakerModalService.setSelectedCarMaker(carMaker);
    const modalElement = document.getElementById('kt_modal_car_maker_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteCarMaker(carMaker: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${carMaker.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(carMaker);
      }
    });
  }

  private performDelete(carMaker: any): void {
    const carMakerId = carMaker._id || carMaker.id;
    this.subscriptions.push(
      this._carMakerService.DeleteDataById(carMakerId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Car maker has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadCarMakers(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete car maker.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting car maker.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onCarMakerAdded(): void {
    console.log('Car maker added, refreshing list...');
    this.loadCarMakers(this.currentPage, this.searchTerm, this.filters);
  }

  onCarMakerUpdated(): void {
    console.log('Car maker updated, refreshing list...');
    this.loadCarMakers(this.currentPage, this.searchTerm, this.filters);
  }

  // Form field change handlers
  onFieldChange(carMakerId: string, field: string, value: any): void {
    if (!this.formData[carMakerId]) {
      this.formData[carMakerId] = {};
    }

    this.formData[carMakerId][field] = value;

    // Check if this car maker is already in changed rows
    const existingIndex = this.changedRows.findIndex(row => row.id === carMakerId);

    if (existingIndex >= 0) {
      // Update existing changed row
      this.changedRows[existingIndex] = {
        id: carMakerId,
        ...this.formData[carMakerId]
      };
    } else {
      // Add new changed row
      this.changedRows.push({
        id: carMakerId,
        ...this.formData[carMakerId]
      });
    }

    this.hasChanges = this.changedRows.length > 0;
  }

  onSave(): void {
    if (this.changedRows.length === 0) {
      return;
    }

    const payload = {
      data: this.changedRows
    };

    this.isLoading = true;

    this.subscriptions.push(
      this._carMakerService.BulkUpdate('', payload).subscribe({
        next: (response) => {
          console.log('Car makers updated successfully:', response);
          Swal.fire({
            title: 'Success!',
            text: `${this.changedRows.length} car maker(s) updated successfully.`,
            icon: 'success',
            confirmButtonText: 'OK'
          }).then(() => {
            // Reset changes after successful save
            this.hasChanges = false;
            this.changedRows = [];
            this.isLoading = false;
            // Reload to get updated data
            this.loadCarMakers(this.currentPage, this.searchTerm, this.filters);
          });
        },
        error: (error) => {
          console.error('Error updating car makers:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'Failed to update car makers. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          this.isLoading = false;
        }
      })
    );
  }

  // Helper methods for form data access
  getFormValue(carMakerId: string, field: string): any {
    return this.formData[carMakerId]?.[field] || false;
  }

  setFormValue(carMakerId: string, field: string, value: any): void {
    if (!this.formData[carMakerId]) {
      this.formData[carMakerId] = {};
    }
    this.formData[carMakerId][field] = value;
    this.onFieldChange(carMakerId, field, value);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(carMakerId: string): boolean {
    return this.selectedCarMakers.includes(carMakerId);
  }

  isAllSelected(): boolean {
    return this.carMakers.length > 0 && this.selectedCarMakers.length === this.carMakers.length;
  }

  isIndeterminate(): boolean {
    return this.selectedCarMakers.length > 0 && this.selectedCarMakers.length < this.carMakers.length;
  }

  toggleSelection(carMakerId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedCarMakers.includes(carMakerId)) {
        this.selectedCarMakers.push(carMakerId);
      }
    } else {
      this.selectedCarMakers = this.selectedCarMakers.filter(id => id !== carMakerId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedCarMakers = this.carMakers.map(carMaker => carMaker._id);
    } else {
      this.selectedCarMakers = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-car-maker-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-car-maker-table-toolbar="selected"]');

    if (this.selectedCarMakers.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedCarMakers(): void {
    if (this.selectedCarMakers.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedCarMakers.length} selected car maker(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedCarMakers.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._carMakerService.BulkDelete(this.selectedCarMakers).subscribe({
        next: () => {
          this.loadCarMakers();
          this.selectedCarMakers = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected car makers have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting car makers:', error);
          Swal.fire('Error!', 'Failed to delete selected car makers.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
